import React, {useCallback, useState} from 'react';

import styled from 'styled-components';

import {AddUserToTeamPopover} from 'app/components/general/GenericDocumentActionButtons/AddUserToTeamPopover';
import {useCurrentUser} from 'app/current-user/api';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {ConfirmFormPopover} from 'app/general/widgets/ConfirmPopover';
import {useAccountSettings} from 'app/settings/api';
import {
  useRemoveUser,
  useResendUserInvitation,
  useSetReadonlyUser,
  useUpdateUser,
} from 'app/user/api';
import {EditExpiryTokenTimePopover} from 'app/user/components/EditExpiryTokenTimePopover';
import {gt} from 'app/utils/i18n';

import type {
  ActionButtonBuilderType,
  ActionMenuItemType,
} from 'app/general/widgets/ActionsButton';
import type {UserSerializedType} from 'sweet-types';

export type ActionsContextType = 'dashboard' | 'invite-dashboard';

interface ActionDefinitionType extends Omit<ActionMenuItemType, 'key'> {
  condition?: () => boolean;
  hideContexts?: Array<ActionsContextType>;
  key?: string;
}

interface Props {
  children?: React.ReactElement | ActionButtonBuilderType;
  pageContext: ActionsContextType;
  user: UserSerializedType;
}

/* a button which opens a dropdown menu with avilable actions for the current document */
export function UserActionsButton({
  children,
  pageContext,
  user,
}: Props): JSX.Element {
  const [isShowing, setShow] = useState<
    null | 'change-expiry' | 'teams' | 'remove'
  >(null);

  const close = () => setShow(null);

  const {data: settings} = useAccountSettings(undefined, {
    refetchOnMountOrArgChange: false,
  });
  const {data: currentUser} = useCurrentUser(undefined, {
    refetchOnMountOrArgChange: false,
  });

  const [updateUser] = useUpdateUser();
  const [resendUserInvitation] = useResendUserInvitation();
  const [removeUser] = useRemoveUser();
  const [setReadOnly] = useSetReadonlyUser();

  const [currButtonRef, setCurrButtonRef] = useState<null | HTMLElement>(null);

  const setButtonRef = useCallback((newButtonRef) => {
    if (newButtonRef != currButtonRef) {
      setCurrButtonRef(newButtonRef);
    }
  }, []);

  const isSuperManager = user.is_super_manager;
  const isSuperTeammate = user.is_super_teammate;
  const isRegularMember = !isSuperManager && !isSuperTeammate;
  const currentIsSuperManager = currentUser?.is_super_manager === true;
  const isGuest = user.is_guest_until != null;

  const availableActions = React.useMemo(() => {
    const actionsDefinitions: Array<ActionDefinitionType> = [
      {
        text: gt('Remove'),
        icon: 'fa fa-times',
        callback: () => setShow('remove'),
        condition: () => currentIsSuperManager,
      },
      {
        text: gt('Update access expiration'),
        icon: 'far fa-clock',
        callback: () => setShow('change-expiry'),
        condition: () => isGuest && currentIsSuperManager,
      },
      {
        text: gt('Resend Invitation'),
        icon: 'far fa-envelope',
        callback: () => resendUserInvitation({to_user_id: user.id}).unwrap(),
        condition: () => !user.is_active,
      },
      {
        text: user.restrict_saml_only_login
          ? gt('Regular email/password login only')
          : gt('Restrict to SSO login only'),
        icon: 'far fa-sign-in',
        callback: () =>
          updateUser({
            id: user.id,
            restrict_saml_only_login: !user.restrict_saml_only_login,
          }).unwrap(),
        condition: () =>
          settings?.is_saml === true &&
          !user.is_saml_account &&
          !isGuest &&
          currentIsSuperManager,
      },
      {
        text: gt('Teams'),
        icon: 'fa fa-users',
        callback: () => setShow('teams'),
        condition: () => !isGuest && currentIsSuperManager,
      },
      {
        text: (
          <span title={gt("Set access to Read Only for this member's teams")}>
            {gt('Set Read Only Access')}
          </span>
        ),
        icon: 'fa fa-user-lock',
        callback: () => setReadOnly({url: user.url}),
        condition: () => !isGuest && currentIsSuperManager,
      },

      {
        text: gt('Regular Member'),
        icon: getIconFor(isRegularMember),
        key: 'regular-member',
        disabled: isRegularMember,
        callback: () =>
          updateUser({
            id: user.id,
            is_super_teammate: false,
            is_super_manager: false,
          }).unwrap(),
        condition: () => !isGuest && currentIsSuperManager,
      },
      {
        text: gt('Super Teammate'),
        icon: getIconFor(isSuperTeammate),
        key: 'super-teammate',
        disabled: isSuperTeammate,
        condition: () => !isGuest && currentIsSuperManager,
        callback: () =>
          updateUser({
            id: user.id,
            is_super_teammate: true,
            is_super_manager: false,
          }).unwrap(),
      },
      {
        text: (
          <span>
            <StyledSuperManagerIconI /> {gt('Super Manager')}
          </span>
        ),
        icon: getIconFor(isSuperManager),
        key: 'super-manager',
        disabled: isSuperManager,
        condition: () => !isGuest && currentIsSuperManager,
        callback: () =>
          updateUser({
            id: user.id,
            is_super_teammate: false,
            is_super_manager: true,
          }).unwrap(),
      },
    ];

    return actionsDefinitions
      .map((item) => ({
        key: item.key || item.icon,
        condition: (): boolean => true,
        hideContexts: [] as Array<ActionsContextType>,
        ...item,
      }))
      .filter(
        (item) => item.condition() && !item.hideContexts.includes(pageContext)
      )
      .map(
        ({icon, text, callback, key, disabled}): ActionMenuItemType => ({
          icon,
          text,
          disabled,
          callback,
          key,
        })
      );
  }, [user, pageContext]);

  // these props are used on all popovers
  const popoversOverlayProps = {
    target: currButtonRef as HTMLElement,
    trigger: 'legacy',
    placement: 'bottom' as const,
    isOpen: true,
    toggle: close,
  };

  return (
    <>
      <ActionsButton ref={setButtonRef} actions={availableActions}>
        {children}
      </ActionsButton>
      {isShowing == null || currButtonRef == null ? null : isShowing ===
        'change-expiry' ? (
        <EditExpiryTokenTimePopover
          overlayProps={popoversOverlayProps}
          user={user}
        />
      ) : isShowing === 'remove' ? (
        <ConfirmFormPopover
          overlayProps={popoversOverlayProps}
          text={gt('Remove member from account?')}
          confirmText={gt('Yes')}
          busyText={gt('Removing...')}
          onConfirm={() => removeUser(user.url).unwrap()}
          isIdle={true}
        />
      ) : isShowing === 'teams' ? (
        <AddUserToTeamPopover overlayProps={popoversOverlayProps} user={user} />
      ) : null}
    </>
  );
}

const getIconFor = (condition: boolean): string =>
  condition ? 'far fa-check-square' : 'far fa-square';

const StyledSuperManagerIconI = styled.i.attrs({
  className: 'fa fa-star text-primary',
})``;
